import type { ItemStack, BlockTextureRender } from 'minecraft-inventory/src/types'
import { flat } from '@xmcl/text-component'
import PItem from 'prismarine-item'
import type { Item } from 'prismarine-item'
import { simplify } from 'prismarine-nbt'
import { renderSlot } from 'minecraft-renderer/src/three/renderSlot'
import { getItemModelName, getItemNameRaw, RenderItem } from '../../mineflayer/items'
import { messageToString } from '../../chatUtils'
import { inventoryBundledConfig } from './inventoryTexturesConfig'

// ----- Atlas sprite extraction (for item textures with resource pack support) -----

const spriteCache = new Map<string, string>()

/** Clear sprite extraction cache (call when atlases are updated). */
export function clearInventoryCaches (): void {
  spriteCache.clear()
  inventoryBundledConfig.resetRenderedSlots()
}

function getAtlas (texture: string): CanvasImageSource | null {
  if (!appViewer?.resourcesManager) return null
  const r = appViewer.resourcesManager
  if (texture === 'gui') return (r.currentResources?.guiAtlas?.image ?? null) as unknown as CanvasImageSource | null
  if (texture === 'items') return (r.currentResources?.itemsAtlasImage ?? null) as unknown as CanvasImageSource | null
  if (texture === 'blocks') return (r.currentResources?.blocksAtlasImage ?? null) as unknown as CanvasImageSource | null
  return null
}

/** Get atlas source suitable for the block renderer (accepts string data URLs). */
function getAtlasForBlockRenderer (texture: string): HTMLImageElement | string | null {
  if (!appViewer?.resourcesManager) return null
  const r = appViewer.resourcesManager
  if (texture === 'blocks') return (r.blocksAtlasParser?.latestImage ?? null)
  return null
}

/** Extract a single-face sprite from the GUI or items atlas as a data URL. */
export function extractSpriteDataUrl (texture: string, slice: number[]): string | undefined {
  const atlas = getAtlas(texture)
  if (!atlas || !slice) return undefined
  const [x, y, w, h] = slice
  const cacheKey = `${texture}:${x}:${y}:${w}:${h}`
  if (spriteCache.has(cacheKey)) return spriteCache.get(cacheKey)

  try {
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(atlas, x, y, w, h, 0, 0, w, h)
    const url = canvas.toDataURL()
    spriteCache.set(cacheKey, url)
    return url
  } catch {
    return undefined
  }
}

/** Build an isometric BlockTextureRender from blockData returned by renderSlot. */
export function buildBlockTexture (blockData: Record<string, { slice: number[] } | undefined>): BlockTextureRender | undefined {
  const source = getAtlasForBlockRenderer('blocks')
  if (!source) return undefined

  const getFace = (...names: string[]): { slice: [number, number, number, number] } | undefined => {
    for (const n of names) {
      const face = (blockData as any)[n]
      if (face?.slice) return { slice: face.slice as [number, number, number, number] }
    }
    return undefined
  }

  const top = getFace('top', 'up', 'all', 'south', 'east', 'west', 'north')
  if (!top) return undefined
  const left = getFace('left', 'east', 'west', 'all') ?? top
  const right = getFace('right', 'north', 'south', 'all') ?? top

  return {
    source: source as unknown as HTMLImageElement,
    top,
    left,
    right,
  }
}

// ----- Shared texture resolution (single source of truth for all item surfaces) -----

/**
 * Resolve `texture` / `blockTexture` for any item by name + optional nbt/components.
 * Used by buildItemMapper (live slots), enrichItemStack (JEI / recipes).
 * Returns empty object on failure so callers can spread safely.
 */
export function resolveItemTextures (item: {
  name: string
  nbt?: any
  components?: any[]
}): { texture?: string; blockTexture?: BlockTextureRender } {
  if (!appViewer?.resourcesManager?.currentResources) return {}
  try {
    const modelName = getItemModelName(
      { name: item.name, nbt: item.nbt ?? null, components: item.components },
      { 'minecraft:display_context': 'gui' },
      appViewer.resourcesManager,
      appViewer.playerState.reactive
    )
    const slotProps = renderSlot({ modelName, originalItemName: item.name }, appViewer.resourcesManager)
    if (slotProps.blockData) {
      const blockTexture = buildBlockTexture(slotProps.blockData as Record<string, { slice: number[] } | undefined>)
      if (blockTexture) return { blockTexture }
    } else if (slotProps.slice) {
      const texture = extractSpriteDataUrl(slotProps.texture, slotProps.slice)
      if (texture) return { texture }
    }
    return {}
  } catch {
    return {}
  }
}

/**
 * Mutate an ItemStack in place with texture/blockTexture.
 * Convenience wrapper around resolveItemTextures for JEI / recipe callers.
 */
export function enrichItemStack (item: ItemStack & { name?: string; nbt?: any; components?: any[] }): void {
  if (!item.name) return
  const resolved = resolveItemTextures({ name: item.name, nbt: item.nbt, components: (item as any).components })
  if (resolved.texture !== undefined) item.texture = resolved.texture
  if (resolved.blockTexture !== undefined) item.blockTexture = resolved.blockTexture
}

// ----- Item mapper – enriches raw bot slots with textures and display info -----

export function buildItemMapper (version: string) {
  const PrismarineItem = PItem(version)

  return (raw: { type?: number; id?: number; itemId?: number; blockId?: number; count?: number; itemCount?: number; metadata?: number; nbt?: unknown; components?: any[] },
    mapped: ItemStack): ItemStack => {
    try {
      const type = raw.type ?? raw.id ?? raw.itemId ?? raw.blockId ?? mapped.type
      const count = raw.count ?? raw.itemCount ?? mapped.count
      if (type === undefined || type <= 0) return mapped

      const slot = new PrismarineItem(type, count, raw.metadata ?? 0) as Item & RenderItem
      if (raw.nbt) (slot as any).nbt = raw.nbt
      if (raw.components) (slot as any).components = raw.components

      const { texture, blockTexture } = resolveItemTextures({
        name: slot.name,
        nbt: (slot as any).nbt,
        components: (slot as any).components,
      })

      const nameRaw = getItemNameRaw(slot, appViewer.resourcesManager)
      const displayName = nameRaw
        ? flat(nameRaw).map((p: any) => (typeof p === 'string' ? p : p.text)).join('')
        : slot.displayName

      return {
        ...mapped,
        type,
        count,
        name: slot.name,
        displayName,
        texture,
        blockTexture,
        durability: (typeof slot.maxDurability === 'number' && typeof slot.durabilityUsed === 'number')
          ? slot.maxDurability - slot.durabilityUsed
          : undefined,
        maxDurability: (slot.maxDurability ?? undefined) as number | undefined,
        enchantments: slot.enchants?.map((e: any) => ({ name: e.name, level: e.lvl })),
      }
    } catch {
      return mapped
    }
  }
}

// ----- Texture config – delegates GUI lookups to inventoryBundledConfig -----

export const textureConfig = {
  getGuiTextureUrl: (path: string) => inventoryBundledConfig.getGuiTextureUrl(path),
  getItemTextureUrl (_item: ItemStack) {
    return ''
  },
  getBlockTextureUrl (_item: ItemStack) {
    return ''
  },
}

// ----- Window title formatter – resolves JSON text components to display strings -----

/** Parse a raw window title (JSON string, NBT object, or plain text) into a readable string. */
export function formatWindowTitle (rawTitle: any): string {
  if (rawTitle === null || rawTitle === undefined) return ''
  if (typeof rawTitle === 'string') {
    // Try to parse JSON text component
    if (rawTitle.startsWith('{') || rawTitle.startsWith('"') || rawTitle.startsWith('[')) {
      try {
        return formatWindowTitle(JSON.parse(rawTitle))
      } catch {
        // Not valid JSON — treat as plain text
      }
    }
    return rawTitle.replace(/§[0-9a-fk-or]/gi, '')
  }
  if (typeof rawTitle === 'object') {
    let simplified: any = rawTitle
    try {
      if (rawTitle.type && rawTitle.value !== undefined) {
        simplified = simplify(rawTitle)
      }
    } catch {
      simplified = rawTitle.value ?? rawTitle
    }

    if (typeof simplified === 'string') {
      return formatWindowTitle(simplified)
    }

    if (typeof simplified === 'object' && simplified !== null) {
      try {
        const str = messageToString(simplified)
        if (str && str !== '[object Object]') {
          return str.replace(/§[0-9a-fk-or]/gi, '')
        }
      } catch {}

      try {
        const flattened = flat(simplified)
        if (Array.isArray(flattened) && flattened.length > 0) {
          const text = flattened.map((p: any) => (typeof p === 'string' ? p : p?.text ?? '')).join('')
          if (text && text !== '[object Object]') return text.replace(/§[0-9a-fk-or]/gi, '')
        }
      } catch {}

      let res = ''
      if (typeof simplified.text === 'string') res += simplified.text
      if (typeof simplified.translate === 'string') {
        const lang = (globalThis as any).loadedData?.language
        res += lang?.[simplified.translate] ?? simplified.translate
      }
      if (Array.isArray(simplified.extra)) {
        res += simplified.extra.map(formatWindowTitle).join('')
      }
      if (Array.isArray(simplified.with)) {
        res += simplified.with.map(formatWindowTitle).join(' ')
      }
      if (res && res !== '[object Object]') return res.replace(/§[0-9a-fk-or]/gi, '')
      if (typeof simplified[''] === 'string') return simplified[''].replace(/§[0-9a-fk-or]/gi, '')
    }
  }
  return ''
}
